import {useEffect, useRef, useCallback, useState} from 'react';
import ParticleBackground from '../components/ParticleBackground';
import {WavRecorder, WavStreamPlayer} from '../lib/wavtools/index.js';
import {WavRenderer} from '../utils/wav_renderer';
import {X, Zap, Mic, MessageSquare, ChevronDown, Calendar} from 'react-feather';
import {Button} from '../components/ui/button';
import {Input} from "../components/ui/input"
import {Label} from "../components/ui/label"
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../components/ui/dialog"
import './PatientHistoryPage.scss';
import {chatService} from '../services/api/chatService';
import {environment} from '../config/environment';
import {storage} from '../utils/storage';
import {startSessionTrace, logMessage, endSessionTrace, logError, flushLangfuse} from '../utils/langfuse';
import {Card, CardContent, CardHeader, CardTitle, CardFooter} from "../components/ui/card";
import {useI18n, LanguageSelector} from '../i18n';
import {MessageMarkdown} from '../components/MessageMarkdown';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/CustomDatePicker.scss";

// Add type declaration for webkitAudioContext
declare global {
    interface Window {
        webkitAudioContext: typeof AudioContext;
    }
}

interface WebRTCMessage {
    type: string;
    session?: any;
    item?: any;
    response?: any;
    trackId?: string;
    offset?: number;
    error?: any;
    transcript?: string;
    delta?: string;
    content?: string;
    delegation_id?: string | null;
    reason?: string;
}

const ANZU_OPENING_DIRECTIVE =
    'Greet the patient by name and begin the pre-consultation intake.';

function isMicrophonePermissionError(error: unknown): boolean {
    const name = (error as { name?: string })?.name;
    return name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'NotFoundError';
}

interface AudioState {
    stream: MediaStream | null;
    audioContext: AudioContext | null;
    audioTrack: MediaStreamTrack | null;
}

interface RealtimeEvent {
    time: string;
    source: 'client' | 'server';
    count?: number;
    event: { [key: string]: any };
}

interface PatientHistoryPageProps {
    onLogout: () => Promise<void>;
}

interface FormattedContent {
    transcript?: string;
    text?: string;
    tool?: any;
    output?: string;
    audio?: Uint8Array;
}

interface ConversationItem {
    id: string;
    type: string;
    role?: string;
    formatted: FormattedContent;
    object: string;
}

const ICE_GATHERING_TIMEOUT_MS = 3000;

async function gatherOfferSdp(pc: RTCPeerConnection): Promise<string> {
    if (pc.iceGatheringState !== 'complete') {
        await new Promise<void>((resolve) => {
            let timer: number | undefined;

            const finish = () => {
                pc.removeEventListener('icegatheringstatechange', onChange);
                window.clearTimeout(timer);
                resolve();
            };

            const onChange = () => {
                if (pc.iceGatheringState === 'complete') {
                    finish();
                }
            };

            timer = window.setTimeout(finish, ICE_GATHERING_TIMEOUT_MS);
            pc.addEventListener('icegatheringstatechange', onChange);

            if (pc.iceGatheringState === 'complete') {
                finish();
            }
        });
    }

    return pc.localDescription?.sdp ?? '';
}

const CONSULT_LANGUAGE_KEYS = {
    en: 'languages.en',
    id: 'languages.id',
    hi: 'languages.hi',
    zh: 'languages.zh',
} as const;

type ConsultLanguage = keyof typeof CONSULT_LANGUAGE_KEYS;


export function PatientHistoryPage({onLogout}: PatientHistoryPageProps) {
    const {t, applyBookedLocale} = useI18n();
    const bookedLanguage = storage.getAuthData()?.session?.language ?? null;
    const bookedLanguageKey = bookedLanguage && bookedLanguage in CONSULT_LANGUAGE_KEYS
        ? CONSULT_LANGUAGE_KEYS[bookedLanguage as ConsultLanguage]
        : null;

    useEffect(() => {
        applyBookedLocale(bookedLanguage);
    }, [applyBookedLocale, bookedLanguage]);

    /**
     * Ask user for API Key
     * If we're using the local relay server, we don't need this
     */


    /**
     * Instantiate:
     * - WavRecorder (speech input)
     * - WavStreamPlayer (speech output)
     */
    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const dataChannelRef = useRef<RTCDataChannel | null>(null);
    const lastDeltaRoleRef = useRef<'user' | 'assistant' | null>(null);
    const textRunIdRef = useRef<string | null>(null);
    const openingGreetingRef = useRef<string>('');
    const itemsRef = useRef<ConversationItem[]>([]);
    const sendQueueRef = useRef<Promise<void>>(Promise.resolve());
    const audioStateRef = useRef<AudioState>({
        stream: null,
        audioContext: null,
        audioTrack: null
    });
    const audioElementRef = useRef<HTMLAudioElement | null>(null);

    /**
     * Realtime voice usage metering (issue #26). The conversation runs
     * browser<->OpenAI directly, so the backend can't see token/audio usage. We
     * accumulate cumulative usage here from `response.done` events and flush it to
     * the backend periodically, on unload, and at session end. Keyed by run_id so
     * every flush upserts the same server-side flow.
     */
    const usageMeterRef = useRef<{
        runId: string | null;
        sessionId: number | null;
        inputTextTokens: number;
        outputTextTokens: number;
        inputAudioTokens: number;
        outputAudioTokens: number;
        audioConnectedAt: number | null; // epoch ms; whisper-minutes ≈ connected audio time
    }>({
        runId: null,
        sessionId: null,
        inputTextTokens: 0,
        outputTextTokens: 0,
        inputAudioTokens: 0,
        outputAudioTokens: 0,
        audioConnectedAt: null,
    });
    const usageFlushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const wavRecorderRef = useRef<WavRecorder>(
        new WavRecorder({sampleRate: 24000})
    );
    const wavStreamPlayerRef = useRef<WavStreamPlayer>(
        new WavStreamPlayer({sampleRate: 24000})
    );

    /**
     * References for
     * - Rendering audio visualization (canvas)
     * - Autoscrolling event logs
     * - Timing delta for event log displays
     */
    const clientCanvasRef = useRef<HTMLCanvasElement>(null);
    const serverCanvasRef = useRef<HTMLCanvasElement>(null);
    const eventsScrollHeightRef = useRef(0);
    const eventsScrollRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<string>(new Date().toISOString());

    /**
     * All of our variables for displaying application state
     * - items are all conversation items (dialog)
     * - realtimeEvents are event logs, which can be expanded
     * - memoryKv is for set_memory() function
     * - coords, marker are for get_weather() function
     */
    const [items, setItems] = useState<ConversationItem[]>([]);
    itemsRef.current = items;
    const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isEndingSession, setIsEndingSession] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [isPreparingSummary, setIsPreparingSummary] = useState(false);
    const [textMessage, setTextMessage] = useState('');
    const [patientInfo, setPatientInfo] = useState({
        name: '',
        dateOfBirth: '',
        gender: ''
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const datePickerRef = useRef<any>(null);
    const [formErrors, setFormErrors] = useState({
        name: '',
        dateOfBirth: '',
        gender: '',
        communicationMode: ''
    });
    const [isConnecting, setIsConnecting] = useState(false);
    const [pendingTurns, setPendingTurns] = useState(0);
    const isSendingText = pendingTurns > 0;
    const [textError, setTextError] = useState<string | null>(null);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [communicationMode, setCommunicationMode] = useState<'audio' | 'text' | ''>('');
    const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
    const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(10); // 10 seconds countdown
    
    // Timeout refs
    const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<Date>(new Date());

    const VOICE_SESSION_TIMEOUT = 70 * 1000; // 70 seconds total (60s inactivity + 10s warning)
    const TEXT_SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes total
    const SESSION_TIMEOUT = communicationMode === 'text' ? TEXT_SESSION_TIMEOUT : VOICE_SESSION_TIMEOUT;
    const WARNING_BEFORE_TIMEOUT = 10 * 1000; // Show warning 10 seconds before timeout
    
    /**
     * Reset activity timer on user interaction
     */
    const resetActivityTimer = useCallback(() => {
        if (!isConnected) return;
        
        lastActivityRef.current = new Date();
        
        // Clear existing timeouts
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        
        // Hide warning if it's showing
        if (showTimeoutWarning) {
            setShowTimeoutWarning(false);
            setTimeoutCountdown(10);
        }
        
        // Set warning timeout (60 seconds into the 70 second timeout)
        warningTimeoutRef.current = setTimeout(() => {
            setShowTimeoutWarning(true);
            setTimeoutCountdown(10);
            
            // Start countdown
            let countdown = 10;
            countdownIntervalRef.current = setInterval(() => {
                countdown -= 1;
                setTimeoutCountdown(countdown);
                
                if (countdown <= 0) {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                    }
                }
            }, 1000);
        }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);
        
        inactivityTimeoutRef.current = setTimeout(() => {
            console.log(`Session timeout - automatically ending session after ${SESSION_TIMEOUT / 1000}s of inactivity`);
            setShowTimeoutWarning(false); // Close the warning dialog immediately
            disconnectConversation(true); // Auto-save the session
        }, SESSION_TIMEOUT);
    }, [isConnected, showTimeoutWarning, SESSION_TIMEOUT]);

    /**
     * Calculate age from date of birth
     */
    const calculateAge = (dateOfBirth: string): number => {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    /**
     * Utility for formatting the timing of logs
     */
    const formatTime = useCallback((timestamp: string) => {
        const startTime = startTimeRef.current;
        const t0 = new Date(startTime).valueOf();
        const t1 = new Date(timestamp).valueOf();
        const delta = t1 - t0;
        const hs = Math.floor(delta / 10) % 100;
        const s = Math.floor(delta / 1000) % 60;
        const m = Math.floor(delta / 60_000) % 60;
        const pad = (n: number) => {
            let s = n + '';
            while (s.length < 2) {
                s = '0' + s;
            }
            return s;
        };
        return `${pad(m)}:${pad(s)}.${pad(hs)}`;
    }, []);

    /**
     * Flush the accumulated realtime voice usage to the backend (issue #26).
     * Cumulative + idempotent server-side, so it's safe to call repeatedly. No-op
     * until a session has started (run_id present). keepalive=true for unload/end.
     */
    const flushUsage = useCallback((keepalive: boolean = false) => {
        const m = usageMeterRef.current;
        if (!m.runId) return;
        const audioMinutes = m.audioConnectedAt
            ? (Date.now() - m.audioConnectedAt) / 60000
            : 0;
        chatService.reportRealtimeUsage({
            run_id: m.runId,
            session_id: m.sessionId,
            input_text_tokens: m.inputTextTokens,
            output_text_tokens: m.outputTextTokens,
            input_audio_tokens: m.inputAudioTokens,
            output_audio_tokens: m.outputAudioTokens,
            audio_minutes: audioMinutes,
        }, keepalive);
    }, []);

    // Flush usage if the tab is closed/backgrounded mid-session (backstop).
    useEffect(() => {
        const handleHide = () => flushUsage(true);
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') flushUsage(true);
        };
        window.addEventListener('pagehide', handleHide);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('pagehide', handleHide);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [flushUsage]);

    /**
     * Show disclaimer dialog before connecting
     */
    const connectConversation = useCallback(async () => {
        if (!validateForm()) {
            return;
        }
        
        // Show disclaimer dialog first
        setShowDisclaimerDialog(true);
    }, [patientInfo, communicationMode]);

    const postTextTurn = useCallback(async (message: string, history: ConversationItem[]) => {
        const authData = storage.getAuthData();
        const sessionId = authData?.session?.id;
        if (!sessionId) {
            throw new Error('No session available for the typed conversation');
        }

        const response = await chatService.sendAnzuTextMessage({
            session_id: sessionId,
            message,
            messages: history
                .filter(item => item.type === 'message' && item.role && item.formatted?.transcript)
                .map(item => ({role: item.role as string, content: item.formatted.transcript as string})),
            ...(textRunIdRef.current ? {run_id: textRunIdRef.current} : {}),
            patient_name: patientInfo.name,
            patient_age: calculateAge(patientInfo.dateOfBirth).toString(),
            patient_gender: patientInfo.gender,
            ...(authData?.session?.practitioner_type
                ? {specialty: authData.session.practitioner_type}
                : {}),
        });

        const reply = response?.data?.reply;
        if (response?.status !== 'success' || !reply) {
            throw new Error('Anzu did not return a reply');
        }

        textRunIdRef.current = response.data.run_id || textRunIdRef.current;

        setItems(prevItems => [...prevItems, {
            id: `${Date.now()}-assistant`,
            type: 'message',
            role: 'assistant',
            formatted: {transcript: reply},
            object: 'conversation.item'
        }]);

        logMessage({
            role: 'assistant',
            content: reply,
            messageId: Date.now().toString(),
        });
        resetActivityTimer();
    }, [patientInfo, resetActivityTimer]);

    const startTextSession = useCallback(async () => {
        setPendingTurns(n => n + 1);
        setTextError(null);
        try {
            await postTextTurn('', []);
        } catch (error) {
            console.error('Could not start the typed conversation:', error);
            setTextError('Anzu could not start the conversation. Send a message to try again.');
        } finally {
            setPendingTurns(n => Math.max(0, n - 1));
        }
    }, [postTextTurn]);

    /**
     * Connect to conversation using WebRTC (called after disclaimer consent)
     */
    const actuallyConnectConversation = useCallback(async () => {
        setIsConnecting(true);
        setConnectionError(null);
        setShowDisclaimerDialog(false);

        try {
            // Get auth data for Langfuse tracing
            const authData = storage.getAuthData();
            const sessionId = authData?.session?.id || 0;
            const patientId = authData?.user?.id || 0;
            const practitionerType = authData?.session?.practitioner_type;

            // Start Langfuse session trace
            startSessionTrace({
                sessionId,
                patientId,
                patientName: patientInfo.name,
                patientAge: calculateAge(patientInfo.dateOfBirth),
                patientGender: patientInfo.gender,
                communicationMode: communicationMode as 'audio' | 'text',
                practitionerType,
            });

            // First send patient info to backend
            await chatService.updatePatientInfo({
                name: patientInfo.name,
                age: calculateAge(patientInfo.dateOfBirth),
                dateOfBirth: patientInfo.dateOfBirth,
                gender: patientInfo.gender
            });

            if (communicationMode === 'text') {
                setIsConnecting(false);
                setIsConnected(true);
                setItems([]);
                await startTextSession();
                return;
            }

            // Then proceed with WebRTC setup
            await setupWebRTC();
            setIsConnecting(false);
            setIsConnected(true);
            setRealtimeEvents([]);

            const wavStreamPlayer = wavStreamPlayerRef.current;

            await wavStreamPlayer.connect();
            const audioContext = wavStreamPlayer.context;

            if (audioContext) {
                console.log('AudioContext state:', audioContext.state);
                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                }
            }
        } catch (error) {
            console.error('Error during conversation connection:', error);
            // Log error to Langfuse
            logError('connection_error', error instanceof Error ? error.message : 'Unknown error');

            if (isMicrophonePermissionError(error)) {
                setConnectionError(t('errors.micPermission'));
                setIsConnecting(false);
                return;
            }

            await disconnectConversation();
        }
    }, [patientInfo, communicationMode, startTextSession, t]);

    /**
     * Disconnect and cleanup WebRTC resources
     */
    const disconnectConversation = useCallback(async (shouldSaveHistory: boolean = true) => {
        try {
            if (shouldSaveHistory) {
                setIsEndingSession(true);
                setShowThankYou(true);
            }

            // Clean up audio element
            if (audioElementRef.current) {
                audioElementRef.current.srcObject = null;
                audioElementRef.current = null;
            }

            // Send chat history only if requested
            if (shouldSaveHistory) {
                const completedMessages = items
                    .filter(item =>
                        item.type === 'message' &&
                        item.formatted?.transcript &&
                        item.role
                    )
                    .map(item => ({
                        role: item.role,
                        transcript: item.formatted.transcript
                    }));

                if (completedMessages.length > 0) {
                    setIsPreparingSummary(true);
                    try {
                        await chatService.sendChatHistory(completedMessages);
                        // End Langfuse session trace with success
                        endSessionTrace(completedMessages.length, 'success');
                    } catch (error) {
                        console.error('Error sending chat history:', error);
                        // End Langfuse session trace with failure
                        endSessionTrace(completedMessages.length, 'failed');
                    } finally {
                        setIsPreparingSummary(false);
                    }
                } else {
                    // End session even if no messages
                    endSessionTrace(0, 'success');
                }
            }

            // Stop periodic usage flushing and send the final cumulative usage (issue #26).
            if (usageFlushTimerRef.current) {
                clearInterval(usageFlushTimerRef.current);
                usageFlushTimerRef.current = null;
            }
            flushUsage(true);

            // Cleanup WebRTC
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }

            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }

            // Cleanup audio
            const {stream, audioTrack, audioContext} = audioStateRef.current;
            if (audioTrack) {
                audioTrack.stop();
            }
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            if (audioContext) {
                await audioContext.close();
            }

            // Reset state
            audioStateRef.current = {
                stream: null,
                audioContext: null,
                audioTrack: null
            };

            // Clean up timeout timers
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
                warningTimeoutRef.current = null;
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
            
            // Reset UI state
            setIsConnected(false);
            setIsConnecting(false);
            setRealtimeEvents([]);
            setItems([]);
            setShowTimeoutWarning(false);
            setTimeoutCountdown(10);
            setPendingTurns(0);
            sendQueueRef.current = Promise.resolve();
            setTextError(null);
            setConnectionError(null);
            lastDeltaRoleRef.current = null;
            textRunIdRef.current = null;
            openingGreetingRef.current = '';

            // Trigger logout after delay only if saving history
            if (shouldSaveHistory) {
                setTimeout(() => {
                    onLogout();
                }, 2000);
            }

        } catch (error) {
            console.error('Error during conversation disconnect:', error);
        }
    }, [onLogout, items]);

    /**
     * Flush Langfuse on component unmount
     */
    useEffect(() => {
        return () => {
            flushLangfuse();
        };
    }, []);

    /**
     * Auto-scroll the event logs
     */
    useEffect(() => {
        if (eventsScrollRef.current) {
            const eventsEl = eventsScrollRef.current;
            const scrollHeight = eventsEl.scrollHeight;
            // Only scroll if height has just changed
            if (scrollHeight !== eventsScrollHeightRef.current) {
                eventsEl.scrollTop = scrollHeight;
                eventsScrollHeightRef.current = scrollHeight;
            }
        }
    }, [realtimeEvents]);

    /**
     * Auto-populate patient information from stored auth data
     */
    useEffect(() => {
        const authData = storage.getAuthData();
        if (authData?.user) {
            const userData = authData.user;
            
            // Determine the preferred name
            let preferredName = '';
            if (userData.preferred_name) {
                // Use preferred_name if it exists
                preferredName = userData.preferred_name;
            } else if (userData.name) {
                // Extract first name from full name if preferred_name is null
                // Remove common titles (Mr, Mrs, Miss, Ms, Dr, Prof, etc.)
                const titles = /^(Mr\.?|Mrs\.?|Miss|Ms\.?|Dr\.?|Prof\.?|Professor|Sir|Lady|Lord)\s+/i;
                const nameWithoutTitle = userData.name.trim().replace(titles, '');
                
                // Get the first word from the name (after removing title)
                preferredName = nameWithoutTitle.split(' ')[0];
            }
            
            setPatientInfo(prev => ({
                name: preferredName || prev.name,
                dateOfBirth: userData.date_of_birth || prev.dateOfBirth,
                gender: userData.gender || prev.gender
            }));
        }
    }, []); // Run only once on component mount

    /**
     * Auto-scroll the conversation logs
     */
    useEffect(() => {
        const conversationEls = [].slice.call(
            document.body.querySelectorAll('[data-conversation-content]')
        );
        for (const el of conversationEls) {
            const conversationEl = el as HTMLDivElement;
            conversationEl.scrollTop = conversationEl.scrollHeight;
        }
    }, [items]);

    /**
     * Set up render loops for the visualization canvas
     */
    useEffect(() => {
        let isLoaded = true;

        const wavRecorder = wavRecorderRef.current;
        const clientCanvas = clientCanvasRef.current;
        let clientCtx: CanvasRenderingContext2D | null = null;

        const wavStreamPlayer = wavStreamPlayerRef.current;
        const serverCanvas = serverCanvasRef.current;
        let serverCtx: CanvasRenderingContext2D | null = null;

        const render = () => {
            if (isLoaded) {
                if (clientCanvas) {
                    if (!clientCanvas.width || !clientCanvas.height) {
                        clientCanvas.width = clientCanvas.offsetWidth;
                        clientCanvas.height = clientCanvas.offsetHeight;
                    }
                    clientCtx = clientCtx || clientCanvas.getContext('2d');
                    if (clientCtx) {
                        clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
                        const result = wavRecorder.recording
                            ? wavRecorder.getFrequencies('voice')
                            : {values: new Float32Array([0])};
                        WavRenderer.drawBars(
                            clientCanvas,
                            clientCtx,
                            result.values,
                            '#0099ff',
                            10,
                            0,
                            8
                        );
                    }
                }
                if (serverCanvas) {
                    if (!serverCanvas.width || !serverCanvas.height) {
                        serverCanvas.width = serverCanvas.offsetWidth;
                        serverCanvas.height = serverCanvas.offsetHeight;
                    }
                    serverCtx = serverCtx || serverCanvas.getContext('2d');
                    if (serverCtx) {
                        serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
                        const result = wavStreamPlayer.analyser
                            ? wavStreamPlayer.getFrequencies('voice')
                            : {values: new Float32Array([0])};
                        WavRenderer.drawBars(
                            serverCanvas,
                            serverCtx,
                            result.values,
                            '#009900',
                            10,
                            0,
                            8
                        );
                    }
                }
                window.requestAnimationFrame(render);
            }
        };
        render();

        return () => {
            isLoaded = false;
        };
    }, []);

    // Event handler definitions moved outside useEffect
    const handleError = useCallback((error: Error) => {
        console.error('WebRTC error:', error);
        // If connection error, try to reconnect
        if (error.message === 'Connection lost') {
            setupWebRTC().catch(console.error);
        }
    }, []);

    const appendTranscriptDelta = useCallback((role: 'user' | 'assistant', delta: string) => {
        if (!delta) return;

        setItems(prevItems => {
            const last = prevItems[prevItems.length - 1];
            if (lastDeltaRoleRef.current === role && last && last.role === role) {
                return [...prevItems.slice(0, -1), {
                    ...last,
                    formatted: {
                        ...last.formatted,
                        transcript: `${last.formatted.transcript || ''}${delta}`
                    }
                }];
            }
            return [...prevItems, {
                id: `${Date.now()}-${role}`,
                type: 'message',
                role,
                formatted: {transcript: delta},
                object: 'conversation.item'
            }];
        });

        lastDeltaRoleRef.current = role;
        resetActivityTimer();
    }, [resetActivityTimer]);

    const sendLiveEvent = useCallback((event: WebRTCMessage) => {
        if (dataChannelRef.current?.readyState === 'open') {
            dataChannelRef.current.send(JSON.stringify(event));
            console.log('Sent GPT-Live event:', event);
        }
    }, []);

    const handleLiveEvent = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data) as WebRTCMessage;
            console.log('Received GPT-Live event:', message);

            switch (message.type) {
                case 'session.started':
                    sendLiveEvent({
                        type: 'session.commentary.append',
                        content: openingGreetingRef.current || ANZU_OPENING_DIRECTIVE,
                        delegation_id: null
                    });
                    resetActivityTimer();
                    break;

                case 'session.input_transcript.delta':
                    appendTranscriptDelta('user', message.delta || '');
                    break;

                case 'session.output_transcript.delta':
                    appendTranscriptDelta('assistant', message.delta || '');
                    break;

                case 'session.closed':
                    console.log('GPT-Live session closed:', message.reason);
                    disconnectConversation(true);
                    break;

                case 'error':
                    handleError(new Error(message.error?.message || 'Unknown GPT-Live error'));
                    break;

                default:
                    console.log('Unhandled GPT-Live event:', message.type);
            }
        } catch (error) {
            console.error('Error processing GPT-Live event:', error);
        }
    }, [appendTranscriptDelta, disconnectConversation, handleError, resetActivityTimer, sendLiveEvent]);

    /**
     * Core WebRTC setup and event handlers
     */
    useEffect(() => {
        // Setup WebRTC event handlers
        const handleConnectionStateChange = () => {
            const state = peerConnectionRef.current?.connectionState;
            console.log('WebRTC connection state:', state);

            if (state === 'connected') {
                setIsConnected(true);
                // session.update is sent from onDataChannelOpen once the data
                // channel is writable; sending it here too would race and
                // produce a conflicting second update.
            } else if (state === 'disconnected' || state === 'failed') {
                setIsConnected(false);
            }
        };


        // Set up event listeners
        if (peerConnectionRef.current) {
            peerConnectionRef.current.onconnectionstatechange = handleConnectionStateChange;
        }

        if (dataChannelRef.current) {
            dataChannelRef.current.addEventListener('message', handleLiveEvent);
        }

        // Cleanup function
        return () => {
            if (peerConnectionRef.current) {
                peerConnectionRef.current.onconnectionstatechange = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.removeEventListener('message', handleLiveEvent);
            }
        };
    }, [handleLiveEvent]);

    /**
     * WebRTC setup functions
     */
    const setupWebRTC = async () => {
        try {
            // Get practitioner_type from stored auth data
            const authData = storage.getAuthData();
            const practitionerType = authData?.session?.practitioner_type;
            const bookedSessionId = authData?.session?.id;

            // Create and configure RTCPeerConnection
            const configuration = {
                iceServers: [
                    {urls: 'stun:stun.l.google.com:19302'}
                ]
            };
            peerConnectionRef.current = new RTCPeerConnection(configuration);

            // Setup audio only if in audio mode
            if (communicationMode === 'audio') {
                await setupAudio();
            }

            // Setup data channel
            setupDataChannel();


            const offer = await peerConnectionRef.current.createOffer();
            await peerConnectionRef.current.setLocalDescription(offer);

            const offerSdp = await gatherOfferSdp(peerConnectionRef.current);
            if (!offerSdp) {
                throw new Error('Failed to generate an SDP offer');
            }

            const sessionPayload: { patient_name: string; patient_age: string; patient_gender: string; practitioner_type?: string; communication_mode?: string; session_id?: number; sdp: string } = {
                patient_name: patientInfo.name,
                patient_age: calculateAge(patientInfo.dateOfBirth).toString(),
                patient_gender: patientInfo.gender,
                communication_mode: communicationMode,
                sdp: offerSdp,
            };

            // Only include practitioner_type if it exists in the magic link
            if (practitionerType) {
                sessionPayload.practitioner_type = practitionerType;
            }

            if (bookedSessionId) {
                sessionPayload.session_id = bookedSessionId;
            }

            const sessionResponse = await chatService.getChatbotSession(sessionPayload);

            const sdpAnswer = sessionResponse?.data?.transport?.sdp;

            if (!sessionResponse || sessionResponse.status !== 'success' || !sdpAnswer) {
                console.error('Invalid session response:', sessionResponse);
                throw new Error('Invalid session response from server');
            }

            openingGreetingRef.current = sessionResponse.data.opening_greeting || '';

            // Start usage metering for this voice session (issue #26): capture the
            // run_id and reset the cumulative meter, then begin periodic flushes.
            const authDataForUsage = storage.getAuthData();
            usageMeterRef.current = {
                runId: sessionResponse.data.run_id ?? null,
                sessionId: authDataForUsage?.session?.id ?? null,
                inputTextTokens: 0,
                outputTextTokens: 0,
                inputAudioTokens: 0,
                outputAudioTokens: 0,
                audioConnectedAt: communicationMode === 'audio' ? Date.now() : null,
            };
            if (usageFlushTimerRef.current) clearInterval(usageFlushTimerRef.current);
            usageFlushTimerRef.current = setInterval(() => flushUsage(false), 30000);

            // Set remote description
            const answer = {
                type: "answer" as RTCSdpType,
                sdp: sdpAnswer,
            };
            await peerConnectionRef.current.setRemoteDescription(answer);

            // Wait for connection to establish
            await new Promise<void>((resolve, reject) => {
                if (!peerConnectionRef.current) {
                    reject(new Error('PeerConnection not initialized'));
                    return;
                }

                const checkState = () => {
                    const state = peerConnectionRef.current?.connectionState;
                    if (state === 'connected') {
                        resolve();
                    } else if (state === 'failed' || state === 'closed') {
                        reject(new Error(`WebRTC connection failed: ${state}`));
                    }
                };

                peerConnectionRef.current.onconnectionstatechange = checkState;
                // Also check immediately in case we're already connected
                checkState();

                // Set a timeout
                setTimeout(() => reject(new Error('WebRTC connection timeout')), 10000);
            });

            setIsConnected(true);
            console.log('WebRTC connection established successfully');
            
            // Start inactivity timer
            resetActivityTimer();

        } catch (error) {
            console.error('Error setting up WebRTC:', error);
            // Clean up any partial setup
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }
            throw error;
        }
    };

    const setupAudio = async () => {
        if (!peerConnectionRef.current) {
            throw new Error('PeerConnection not initialized');
        }

        // Only proceed with audio setup if in audio mode
        if (communicationMode !== 'audio') {
            return;
        }

        try {
            // Create audio context with fallback
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Request microphone access with constraints
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 24000
                },
                video: false
            };

            // Setup microphone
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            const audioTrack = stream.getAudioTracks()[0];

            if (!audioTrack) {
                throw new Error('No audio track available');
            }

            // Configure audio track
            audioTrack.enabled = true;

            // Add to peer connection
            const sender = peerConnectionRef.current.addTrack(audioTrack, stream);

            // Store audio state
            audioStateRef.current = {
                stream,
                audioContext,
                audioTrack
            };

            // Handle incoming audio stream
            peerConnectionRef.current.ontrack = (event) => {
                // Only handle audio tracks if in audio mode
                if (communicationMode !== 'audio') {
                    return;
                }

                try {
                    const [remoteStream] = event.streams;
                    if (!remoteStream) {
                        console.warn('No remote stream available');
                        return;
                    }

                    // Create audio element only if it doesn't exist and in audio mode
                    if (!audioElementRef.current && communicationMode === 'audio') {
                        const audioEl = document.createElement("audio");
                        audioEl.autoplay = true;
                        audioElementRef.current = audioEl;
                    }

                    // Update the stream source only if audio element exists
                    if (audioElementRef.current) {
                        audioElementRef.current.srcObject = remoteStream;

                        // Ensure audio starts playing
                        const playPromise = audioElementRef.current.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(error => {
                                console.error('Audio playback error:', error);
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error handling remote audio stream:', error);
                }
            };

            console.log('Audio setup completed successfully');
        } catch (error) {
            console.error('Error setting up audio:', error);
            // Clean up any partial setup
            if (audioStateRef.current.stream) {
                audioStateRef.current.stream.getTracks().forEach(track => track.stop());
            }
            if (audioStateRef.current.audioContext) {
                await audioStateRef.current.audioContext.close();
            }
            audioStateRef.current = {
                stream: null,
                audioContext: null,
                audioTrack: null
            };
            throw error;
        }
    };

    const setupDataChannel = () => {
        if (!peerConnectionRef.current) return;

        dataChannelRef.current = peerConnectionRef.current.createDataChannel("oai-events");
        dataChannelRef.current.onopen = onDataChannelOpen;
        // Message listener is added in the useEffect hook to avoid duplicates
    };

    const onDataChannelOpen = () => {
        console.log('Data channel open; waiting for session.started');
    };

    // Update the conversation item rendering
    const renderConversationItem = (item: ConversationItem) => (
        <div
            className="mb-6 last:mb-0"
            key={item.id}
        >
            <div className="flex items-center justify-between mb-1 px-1">
                <div className="text-sm font-medium text-[#6c757d] capitalize">
                    {item.role === 'user'
                        ? t('conversation.you')
                        : item.role === 'assistant'
                            ? t('conversation.anzu')
                            : (item.role || item.type).replaceAll('_', ' ')}
                </div>
            </div>
            <div
                className={`rounded-2xl p-4 border ${item.role === 'user'
                    ? 'bg-[rgba(0,166,147,0.05)] border-[rgba(0,166,147,0.1)] mr-8 rounded-br-sm'
                    : 'bg-[rgba(26,59,93,0.05)] border-[rgba(26,59,93,0.1)] ml-8 rounded-bl-sm'
                }`}
            >
                {item.type === 'function_call_output' && (
                    <div className="whitespace-pre-wrap text-[#2c3e50] text-[0.9375rem] leading-relaxed">
                        {item.formatted.output}
                    </div>
                )}
                {!!item.formatted.tool && (
                    <div
                        className="font-mono text-sm bg-[rgba(0,0,0,0.03)] p-3 rounded-lg my-3 border border-[rgba(0,0,0,0.05)]">
                        {item.formatted.tool.name}(
                        {item.formatted.tool.arguments})
                    </div>
                )}
                {!item.formatted.tool && item.role === 'user' && (
                    <div className="whitespace-pre-wrap text-[#2c3e50] text-[0.9375rem] leading-relaxed">
                        {item.formatted.transcript ||
                            (item.formatted.audio?.byteLength
                                ? '(awaiting transcript)'
                                : item.formatted.text ||
                                '(item sent)')}
                    </div>
                )}
                {!item.formatted.tool && item.role === 'assistant' && (
                    <MessageMarkdown>
                        {item.formatted.transcript ||
                            item.formatted.text ||
                            '(truncated)'}
                    </MessageMarkdown>
                )}
            </div>
        </div>
    );

    const validateForm = () => {
        let isValid = true;
        const errors = {
            name: '',
            dateOfBirth: '',
            gender: '',
            communicationMode: ''
        };

        if (!patientInfo.name.trim()) {
            errors.name = t('form.name.required');
            isValid = false;
        }

        if (!patientInfo.dateOfBirth) {
            errors.dateOfBirth = t('form.dateOfBirth.required');
            isValid = false;
        } else {
            const age = calculateAge(patientInfo.dateOfBirth);
            if (age < 0 || age > 120) {
                errors.dateOfBirth = t('form.dateOfBirth.invalid');
                isValid = false;
            }
        }

        if (!patientInfo.gender) {
            errors.gender = t('form.gender.required');
            isValid = false;
        }

        if (!communicationMode) {
            errors.communicationMode = t('form.mode.required');
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    // Add the sendTextMessage function
    const sendTextMessage = useCallback(() => {
        const text = textMessage.trim();
        if (!text) return;

        // Reset activity timer on user interaction
        resetActivityTimer();

        // Log user text message to Langfuse
        logMessage({
            role: 'user',
            content: text,
            messageId: Date.now().toString(),
        });

        const userItem: ConversationItem = {
            id: `${Date.now()}-user-${Math.random().toString(36).slice(2, 8)}`,
            type: 'message',
            role: 'user',
            formatted: {transcript: text},
            object: 'conversation.item'
        };

        setItems(prevItems => [...prevItems, userItem]);
        setTextMessage('');
        setTextError(null);
        setPendingTurns(n => n + 1);

        sendQueueRef.current = sendQueueRef.current.then(async () => {
            try {
                const history = itemsRef.current.filter(item => item.id !== userItem.id);
                await postTextTurn(text, history);
            } catch (error) {
                console.error('Anzu text turn failed:', error);
                setTextError('Anzu could not reply to that. Please send it again.');
            } finally {
                setPendingTurns(n => Math.max(0, n - 1));
            }
        });
    }, [textMessage, postTextTurn, resetActivityTimer]);

    /**
     * Render the application
     */
    return (
        <div data-component="PatientHistoryPage"
             className="patient-history-page w-full h-screen flex flex-col bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef] overflow-hidden">
            <ParticleBackground/>

            {/* Fixed navigation bar for mobile */}
            <header
                className="sticky top-0 z-20 w-full bg-white/90 backdrop-blur-md shadow-sm border-b border-[#e2e8f0]">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Navigation bar with proper layout */}
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Left side: AIGP Health logo and Doc Assist */}
                        <div className="flex items-center">
                            <img
                                src="/aigp-logo.png"
                                alt="AIGP Health Logo"
                                className="w-12 h-12 md:w-16 md:h-16 object-contain"
                            />
                            <div className="ml-2 md:ml-3">
                                <div
                                    className="font-bold text-base md:text-lg bg-gradient-to-r from-[#1E355C] to-[#356BAD] bg-clip-text text-transparent">
                                    {t('header.brand')}
                                </div>
                            </div>
                        </div>

                        <LanguageSelector />

                        {/* Removed hardcoded clinic-specific content for white-labeling */}
                        {/* Center divider - only visible on md and larger screens */}
                        {/* <div className="hidden md:block h-8 w-px bg-gray-300 mx-4"></div> */}

                        {/* Right side: MediPoint Medical Centre with clinic logo */}
                        {/* <div className="hidden md:flex items-center">
                            <img
                                src="/clinic-logo.png"
                                alt="MediPoint Logo"
                                className="w-8 h-8 object-contain"
                            />
                            <div className="ml-2">
                                <div className="text-[#1E355C] font-medium text-sm">The Doctors Werribee</div>

                            </div>
                        </div> */}

                        {/* Mobile version of clinic info - only visible on small screens */}
                        {/* <div className="flex md:hidden items-center">
                            <img
                                src="/clinic-logo.png"
                                alt="MediPoint Logo"
                                className="w-8 h-8 object-contain"
                            />
                            <div className="ml-2">
                                <div className="text-[#1E355C] font-medium text-xs">MediPoint Medical</div>
                                <div className="text-[#6c757d] text-xs">WOODLANDS BRANCH</div>
                            </div>
                        </div> */}

                        {/* Logout hidden: it called disconnectConversation(false), which
                            discards the intake without submitting it. A patient on a
                            one-time magic link has no account to log out of, and
                            "End Session" already ends the visit while saving. */}
                    </div>
                </div>
            </header>

            {/* Main content area with flex-1 to take available space */}
            <main className="flex-1 w-full max-w-4xl mx-auto p-4 md:p-6 overflow-hidden flex flex-col">
                <Card
                    className="w-full h-full flex flex-col overflow-hidden border-[#e2e8f0] bg-white/90 backdrop-blur-sm">
                    <CardHeader
                        className="p-4 md:p-6 border-b border-[#e2e8f0] flex flex-row items-center justify-between shrink-0">
                        <CardTitle className="text-lg md:text-xl font-semibold text-[#2c3e50]">{t('form.title')}</CardTitle>
                        {isConnected && (
                            <div className="flex items-center gap-2 text-sm md:text-base text-[#356BAD]">
                                <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#356BAD]"></span>
                                <span>{t('form.sessionActive')}</span>
                            </div>
                        )}
                    </CardHeader>

                    <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
                        {showThankYou ? (
                            <div className="flex flex-col items-center justify-center p-8 md:p-10 text-center h-full">
                                <Zap size={32} className={`text-[#356BAD] mb-4 md:size-40 md:mb-6 ${isPreparingSummary ? 'animate-pulse' : ''}`}/>
                                {isPreparingSummary ? (
                                    <>
                                        <p className="text-lg md:text-xl font-medium text-[#2c3e50] mb-2">{t('farewell.preparing')}</p>
                                        <span className="text-sm md:text-base text-[#6c757d]">{t('farewell.preparingHint')}</span>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-lg md:text-xl font-medium text-[#2c3e50] mb-2">{t('farewell.thanks')}</p>
                                        <span className="text-sm md:text-base text-[#6c757d]">{t('farewell.loggingOut')}</span>
                                    </>
                                )}
                            </div>
                        ) : (!isConnected && !isConnecting) ? (
                            (
                                // Patient form with ShadCN components - increased size for iPad
                                <div className="p-4 md:p-6 h-full overflow-y-auto">
                                    <div className="space-y-5 md:space-y-6 max-w-md md:max-w-lg mx-auto">
                                        {bookedLanguageKey && (
                                            <div className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm md:text-base text-[#2c3e50]">
                                                <MessageSquare size={16} className="text-[#356BAD] shrink-0"/>
                                                <span>{t('consult.booked', {language: t(bookedLanguageKey)})}</span>
                                            </div>
                                        )}

                                        {/* Name field */}
                                        <div>
                                            <Label htmlFor="name"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">{t('form.name.label')}</Label>
                                            <Input
                                                id="name"
                                                value={patientInfo.name}
                                                onChange={(e) => {
                                                    setPatientInfo(prev => ({...prev, name: e.target.value}));
                                                    setFormErrors(prev => ({...prev, name: ''}));
                                                }}
                                                placeholder={t('form.name.placeholder')}
                                                className={`w-full rounded-lg border-2 ${formErrors.name ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                } focus-visible:border-[#356BAD] focus-visible:ring-[#356BAD]/10 h-11 md:h-14 md:text-lg md:px-4`}
                                            />
                                            {formErrors.name && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.name}</span>}
                                        </div>

                                        {/* Date of Birth field - now in its own row */}
                                        <div className="relative">
                                            <Label htmlFor="dateOfBirth"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">{t('form.dateOfBirth.label')}</Label>
                                            <div className="relative w-full">
                                                <DatePicker
                                                    ref={datePickerRef}
                                                    id="dateOfBirth"
                                                    selected={patientInfo.dateOfBirth ? new Date(patientInfo.dateOfBirth) : null}
                                                    onChange={(date: Date | null) => {
                                                        if (date) {
                                                            const formattedDate = date.toISOString().split('T')[0];
                                                            setPatientInfo(prev => ({...prev, dateOfBirth: formattedDate}));
                                                            setFormErrors(prev => ({...prev, dateOfBirth: ''}));
                                                        }
                                                        setIsDatePickerOpen(false);
                                                    }}
                                                    open={isDatePickerOpen}
                                                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                                                    onCalendarClose={() => setIsDatePickerOpen(false)}
                                                    onInputClick={() => {
                                                        setIsDatePickerOpen(!isDatePickerOpen);
                                                    }}
                                                    onClickOutside={() => setIsDatePickerOpen(false)}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText={t('form.dateOfBirth.placeholder')}
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    dropdownMode="select"
                                                    maxDate={new Date()}
                                                    minDate={new Date(1900, 0, 1)}
                                                    showMonthDropdown
                                                    popperPlacement="top-start"
                                                    preventOpenOnFocus={true}
                                                    autoFocus={false}
                                                    portalId="root"
                                                    shouldCloseOnSelect={true}
                                                    className={`w-full rounded-lg border-2 px-3 md:px-4 h-11 md:h-14 md:text-lg pr-10 ${formErrors.dateOfBirth ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                    } focus:outline-none focus:border-[#356BAD] focus:ring-2 focus:ring-[#356BAD]/10 bg-white cursor-pointer`}
                                                    wrapperClassName="w-full"
                                                    calendarClassName="date-picker-calendar"
                                                />
                                                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 md:h-6 md:w-6 text-[#6c757d] pointer-events-none z-10" />
                                            </div>
                                            {formErrors.dateOfBirth && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.dateOfBirth}</span>}
                                        </div>

                                        {/* Gender field - improved styling to match other components */}
                                        <div>
                                            <Label htmlFor="gender"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">{t('form.gender.label')}</Label>
                                            <div className={`relative ${formErrors.gender ? 'mb-1' : ''}`}>
                                                <select
                                                    id="gender"
                                                    value={patientInfo.gender}
                                                    onChange={(e) => {
                                                        setPatientInfo(prev => ({...prev, gender: e.target.value}));
                                                        setFormErrors(prev => ({...prev, gender: ''}));
                                                    }}
                                                    className={`w-full h-11 md:h-14 px-3 md:px-4 rounded-lg border-2 appearance-none ${formErrors.gender ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                    } focus:outline-none focus:border-[#356BAD] focus:ring-2 focus:ring-[#356BAD]/10 bg-white text-[#2c3e50] md:text-lg`}
                                                >
                                                    <option value="" className="text-[#6c757d]">{t('form.gender.placeholder')}</option>
                                                    <option value="Male" className="text-[#2c3e50]">{t('form.gender.male')}</option>
                                                    <option value="Female" className="text-[#2c3e50]">{t('form.gender.female')}</option>
                                                </select>
                                                {/* Custom dropdown arrow that matches the theme */}
                                                <div
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 md:pr-4 pointer-events-none">
                                                    <ChevronDown className="h-5 w-5 md:h-6 md:w-6 text-[#6c757d]"/>
                                                </div>
                                            </div>
                                            {formErrors.gender && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.gender}</span>}
                                        </div>

                                        {/* Communication mode with icons added */}
                                        <div>
                                            <Label
                                                className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">{t('form.mode.label')}</Label>
                                            <div className="grid grid-cols-2 gap-3 md:gap-4">
                                                <Button
                                                    type="button"
                                                    variant={communicationMode === 'audio' ? 'default' : 'outline'}
                                                    className={`h-11 md:h-14 rounded-lg font-medium md:text-lg ${communicationMode === 'audio'
                                                        ? 'bg-[#356BAD] text-white border-[#356BAD] shadow-sm'
                                                        : 'border-[#e2e8f0] text-[#2c3e50] hover:border-[#356BAD] hover:text-[#356BAD]'
                                                    }`}
                                                    onClick={() => {
                                                        setCommunicationMode('audio');
                                                        setFormErrors(prev => ({...prev, communicationMode: ''}));
                                                    }}
                                                >
                                                    <Mic className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                    {t('form.mode.voice')}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant={communicationMode === 'text' ? 'default' : 'outline'}
                                                    className={`h-11 md:h-14 rounded-lg font-medium md:text-lg ${communicationMode === 'text'
                                                        ? 'bg-[#356BAD] text-white border-[#356BAD] shadow-sm'
                                                        : 'border-[#e2e8f0] text-[#2c3e50] hover:border-[#356BAD] hover:text-[#356BAD]'
                                                    }`}
                                                    onClick={() => {
                                                        setCommunicationMode('text');
                                                        setFormErrors(prev => ({...prev, communicationMode: ''}));
                                                    }}
                                                >
                                                    <MessageSquare className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                    {t('form.mode.text')}
                                                </Button>
                                            </div>
                                            {formErrors.communicationMode && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.communicationMode}</span>}
                                        </div>

                                        {connectionError && (
                                            <p className="text-sm text-[#dc3545]">{connectionError}</p>
                                        )}

                                        {/* Start New Session button - positioned right after communication mode */}
                                        <div className="pt-2">
                                            <Button
                                                className="w-full h-11 md:h-14 bg-[#356BAD] hover:bg-[#356BAD]/90 text-white font-medium rounded-lg shadow-sm md:text-lg"
                                                onClick={connectConversation}
                                                style={{visibility: isConnecting ? 'hidden' : 'visible'}}
                                            >
                                                <Zap className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                {t('form.start')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="flex flex-col h-full">
                                {/* Scrollable conversation area with scroll-behavior: smooth for better UX */}
                                <div
                                    id="conversation-container"
                                    className="flex-1 p-4 md:p-6 overflow-y-auto scroll-smooth"
                                    style={{
                                        scrollBehavior: 'smooth',
                                    }}
                                >
                                    <div
                                        className="space-y-4 md:space-y-6"
                                        key={`conversation-${items.length}`}
                                    >
                                        {items.length === 0 && (
                                            <div
                                                className="flex flex-col items-center justify-center p-8 md:p-10 text-center">
                                                <Zap size={32}
                                                     className="text-[#356BAD] mb-4 animate-pulse md:size-40 md:mb-6"/>
                                                <p className="text-lg md:text-xl font-medium text-[#2c3e50]">
                                                    {communicationMode === 'text'
                                                        ? t('conversation.emptyText')
                                                        : t('conversation.emptyVoice')}
                                                </p>
                                            </div>
                                        )}
                                        {items.map(renderConversationItem)}
                                        {isSendingText && communicationMode === 'text' && (
                                            <div className="mb-6 last:mb-0">
                                                <div className="flex items-center justify-between mb-1 px-1">
                                                    <div className="text-sm font-medium text-[#6c757d] capitalize">{t('conversation.anzu')}</div>
                                                </div>
                                                <div className="rounded-2xl p-4 border bg-[rgba(26,59,93,0.05)] border-[rgba(26,59,93,0.1)] ml-8 rounded-bl-sm">
                                                    <div className="flex items-center gap-1.5 h-[1.4rem]" aria-label={t('conversation.typing')} role="status">
                                                        <span className="w-2 h-2 rounded-full bg-[#6c757d] animate-bounce" style={{animationDelay: '0ms'}}></span>
                                                        <span className="w-2 h-2 rounded-full bg-[#6c757d] animate-bounce" style={{animationDelay: '150ms'}}></span>
                                                        <span className="w-2 h-2 rounded-full bg-[#6c757d] animate-bounce" style={{animationDelay: '300ms'}}></span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* Invisible element at the bottom that we'll scroll to */}
                                        <div
                                            id="conversation-end"
                                            className="h-1"
                                            ref={(el) => {
                                                if (el && items.length > 0) {
                                                    el.scrollIntoView({behavior: 'smooth'});
                                                }
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Fixed text input at bottom */}
                                {isConnected && communicationMode === 'text' && (
                                    <div className="border-t border-[#e2e8f0] p-4 md:p-5 bg-white">
                                        {textError && (
                                            <p className="mb-2 text-sm text-[#dc3545]">{textError}</p>
                                        )}
                                        <div className="flex items-center gap-2 md:gap-3">
                                            <Input
                                                type="text"
                                                value={textMessage}
                                                onChange={(e) => setTextMessage(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        sendTextMessage();
                                                    }
                                                }}
                                                placeholder={t('conversation.placeholder')}
                                                autoFocus
                                                className="flex-1 h-11 md:h-14 rounded-lg border-2 border-[#e2e8f0] focus-visible:border-[#356BAD] focus-visible:ring-[#356BAD]/10 md:text-lg md:px-4"
                                            />
                                            <Button
                                                onClick={sendTextMessage}
                                                disabled={!textMessage.trim()}
                                                className="h-11 md:h-14 px-4 md:px-6 bg-[#356BAD] hover:bg-[#356BAD]/90 text-white md:text-lg"
                                            >
                                                {t('conversation.send')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>

                    {/* Only show footer when session is connected */}
                    {(isConnected || showThankYou) && (
                        <CardFooter className="p-4 md:p-6 border-t border-[#e2e8f0] shrink-0">
                            <div className="w-full flex justify-center">
                                <Button
                                    variant="outline"
                                    className={`h-11 px-6 border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#dc3545] ${isEndingSession ? 'opacity-70 pointer-events-none' : ''}`}
                                    onClick={() => setShowEndSessionDialog(true)}
                                    disabled={isEndingSession || showThankYou}
                                >
                                    {isEndingSession ? t('session.saving') : t('session.end')}
                                    {!isEndingSession && <X className="ml-2" size={16}/>}
                                </Button>
                            </div>
                        </CardFooter>
                    )}
                </Card>
            </main>

            {/* End Session Confirmation Dialog */}
            <Dialog open={showEndSessionDialog} onOpenChange={setShowEndSessionDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-[#2c3e50]">{t('session.end')}</DialogTitle>
                        <DialogDescription className="text-[#6c757d]">
                            {t('session.confirmBody')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => setShowEndSessionDialog(false)}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            {t('session.continue')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowEndSessionDialog(false);
                                disconnectConversation();
                            }}
                            className="flex-1 sm:flex-none bg-[#dc3545] hover:bg-[#dc3545]/90"
                        >
                            {t('session.end')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AI Disclaimer Dialog */}
            <Dialog open={showDisclaimerDialog} onOpenChange={setShowDisclaimerDialog}>
                <DialogContent className="sm:max-w-[760px] max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-[#2c3e50] mb-4">{t('disclaimer.title')}</DialogTitle>
                        <DialogDescription className="text-[#2c3e50] space-y-4 text-sm leading-relaxed">
                            <p className="font-semibold text-[#dc3545] text-base">
                                {t('disclaimer.important')}
                            </p>

                            <p className="font-semibold">{t('disclaimer.lead')}</p>

                            <ul className="list-disc pl-5 space-y-3">
                                <li>{t('disclaimer.point1')}</li>
                                <li>{t('disclaimer.point2')}</li>
                                <li>{t('disclaimer.point3')}</li>
                                <li>{t('disclaimer.point4')}</li>
                                <li>{t('disclaimer.point5')}</li>
                                <li>{t('disclaimer.point6')}</li>
                                <li>{t('disclaimer.point7')}</li>
                                <li>{t('disclaimer.point8')}</li>
                            </ul>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setShowDisclaimerDialog(false)}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            {t('disclaimer.cancel')}
                        </Button>
                        <Button
                            onClick={actuallyConnectConversation}
                            className="flex-1 sm:flex-none bg-[#356BAD] hover:bg-[#356BAD]/90 text-white"
                        >
                            {t('disclaimer.agree')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Session Timeout Warning Dialog */}
            <Dialog open={showTimeoutWarning} onOpenChange={(open) => {
                if (!open) {
                    // User clicked continue - reset the timer
                    resetActivityTimer();
                }
                setShowTimeoutWarning(open);
            }}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-[#2c3e50]">{t('timeout.title')}</DialogTitle>
                        <DialogDescription className="text-[#6c757d] space-y-3">
                            <p>{t('timeout.inactive')}</p>
                            <p className="font-semibold text-[#dc3545]">
                                {t('timeout.countdown', {seconds: timeoutCountdown})}
                            </p>
                            <p>{t('timeout.hint')}</p>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowTimeoutWarning(false);
                                resetActivityTimer();
                            }}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            {t('session.continue')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowTimeoutWarning(false);
                                disconnectConversation(true);
                            }}
                            className="flex-1 sm:flex-none bg-[#dc3545] hover:bg-[#dc3545]/90"
                        >
                            {t('timeout.endNow')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

